import { ConfigValue } from '@atoms/ConfigPicker/types';
import { setImpersonatingFlag } from '@common/auth/utils';
import { type UCCatalogMetadata } from '@common/catalog/catalogTypes';
import type { ChatHistoryResponse, CopilotTriggerType } from '@common/Copilot/types';
import { AirflowConnection, AirflowEnvironment } from '@common/Fabric/types/airflow';
import { type SparkEnvironmentResponse } from '@common/Fabric/types/spark';
import type {
  JobDeploymentMode,
  JobDeploymentRow,
  ProjectDeployment,
  ProjectDeploymentHistory,
  ProjectRelease,
  ProjectType
} from '@common/project/types';
import { BranchInfoQueryType, CommitInfo, UncommittedEntityInfo } from '@common/queries/git';
import { type AirflowSpecLanguage, DatabricksJobInfo, SpecLanguage } from '@common/types';
import { DependencyType } from '@common/types/dependencyTypes';
import { PrimitiveType } from '@common/types/interimTypes';
import { SessionResponse } from '@common/types/sessionTypes';
import { UISpecElement } from '@common/types/specTypes';
import { Public_Routes } from '@common/url';
import { ProphecyError } from '@prophecy/utils/error';
import { downloadFile } from '@prophecy/utils/file';
import { clearStorage, navigateAndRefresh } from '@prophecy/utils/history';
import queryString from 'query-string';

import { type UserFabricStatus } from '../../context/types';
import {
  ShareDetails,
  AdminConfigType,
  AuthTypes,
  ClusterDatabricksOpsPayload,
  ClusterDatabricksOpsResponse,
  DatabrickUserPasswordPayload,
  ExecutionLog,
  KGCrawlStatusResponse,
  OrchestrationPipelineRunResponse,
  OrchestrationScheduledPipelineResponse,
  ProviderType,
  ReplayDetails,
  BotCheckResponse,
  OrchestrationPipelineRunLogsResponse
} from '../../data/apis/types';
import { BackEndCodeLanguageType, WorkflowModeType } from '../../graphqlTypes/enums';
import { BusinessRule, JobSessionAttachmentSource, SColumn } from '../../redux/types';
import { Schema } from '../../schema/sparkTypes';
import { SQLType } from '../../schema/sqlTypes';
import { ProviderType as SchemaProviderType } from '../../schema/types';
import {
  APIUrls,
  copilotEndpoint,
  getAirflowComponentListEndPoint,
  getExecutionServiceEndPoint,
  getFabricEndPoint,
  getFabricSecretUrl,
  getJobLogListUrl,
  getJobRunListUrl,
  getTranspilerEndpoint,
  getValidateDependencyEndPoint,
  getValidatePyPIDependencyEndPoint,
  getWorkflowComponentListEndPoint,
  hostUrl,
  getOrchestationEndpoint
} from '../../utils/getServerUrl';
import { SHOW_JPMC_POST_LOGIN_COPILOT_POPUP_ACKNOWLEDGED } from '../../utils/localstorage-keys';
import { isReplayMode } from '../../utils/replayToken';
import { isShareMode } from '../../utils/shareToken';
import { ExecutionStatus } from '../execution.types';
import { authorizedFetch } from '../rest/autorizedFetch';
import { makeRestCall } from '../rest/makeRestCall';
import {
  errorHandler,
  Headers,
  makeDeleteCall,
  makeGetCall,
  makePostCall,
  mockXHRResponse,
  uploadFiles,
  XHRResponseType,
  xhrWrapper,
  makePostCallWithoutShareTokens
} from '../util';
import {
  BasicFormType,
  ChangeEntityImportType,
  ComponentInfo,
  ConfigurationInfoType,
  DatasetInfoType,
  ImportResponse,
  InfoCardType,
  JobInfoType,
  PipelineInfoType,
  SourceInfoType,
  TranspilerEntityType,
  UDfInfoType
} from './transpileTypes';

export async function fetchComponentList() {
  return await makeGetCall(getWorkflowComponentListEndPoint());
}

export async function fetchAirflowComponentList() {
  return await makeGetCall(getAirflowComponentListEndPoint());
}

export async function validateDependencyData(data: { artifact: string; repo?: string }) {
  return await makePostCall(getValidateDependencyEndPoint(), data);
}

export async function validateDependencyPypiData(data: { package: string; repo?: string }) {
  return await makePostCall(getValidatePyPIDependencyEndPoint(), data);
}

export async function getSessionList(
  executionUrl: string,
  fabricId: string,
  data?: {
    tabId: string;
    dependencies?: { dependencies: DependencyType[]; projectLanguage?: BackEndCodeLanguageType };
    jobFabricId?: string;
    jobSize?: string;
    attachmentSource?: JobSessionAttachmentSource;
  }
): XHRResponseType<SessionResponse> {
  return await makePostCall(`${getFabricEndPoint(executionUrl, fabricId)}/listSessions`, data);
}

export async function getJobSessionList(
  executionUrl: string,
  fabricId: string,
  data?: {
    tabId: string;
    dependencies?: { dependencies: DependencyType[] };
    jobFabricId?: string;
    jobSize?: string;
    attachmentSource?: JobSessionAttachmentSource;
  }
): XHRResponseType<SessionResponse> {
  return await makePostCall(`${getFabricEndPoint(executionUrl, fabricId)}/listSessionsForInteractiveJob`, data);
}

export async function getDatabricksJobsList(
  executionUrl: string,
  fabricId: string
): XHRResponseType<{ result: DatabricksJobInfo[] }> {
  return await makeGetCall(`${getFabricEndPoint(executionUrl, fabricId)}/jobs`);
}

export async function handleDatabricksClusterAction(
  executionUrl: string,
  fabricId: string,
  data?: ClusterDatabricksOpsPayload
): XHRResponseType<ClusterDatabricksOpsResponse> {
  return await makePostCall(`${getFabricEndPoint(executionUrl, fabricId)}/databricksOperation`, data);
}

export async function deleteLivySession(executionUrl: string, sessionName: string): XHRResponseType<SessionResponse> {
  return await makeDeleteCall(`${getExecutionServiceEndPoint(executionUrl)}/fabrics/sessions/${sessionName}`, {});
}

export async function getFabricSecrets(executionUrl: string, fabricId: string) {
  return await makeGetCall(getFabricSecretUrl(executionUrl, fabricId));
}

export async function saveFabricSecrets(
  executionUrl: string,
  fabricId: string,
  scope: string,
  username: string,
  password: string
) {
  return await makeRestCall(getFabricSecretUrl(executionUrl, fabricId), {
    method: 'PUT',

    body: JSON.stringify({
      scope,
      value1: { key: 'username', value: username },
      value2: { key: 'password', value: password }
    })
  });
}

export async function createSession({
  executionUrl,
  fabricId,
  poolId,
  clusterId,
  clusterName
}: {
  executionUrl: string;
  fabricId: string;
  poolId?: string;
  clusterId?: string;
  clusterName?: string;
}) {
  let payload: {
    fabricId: string;
    poolId?: string;
    clusterId?: string;
    clusterName?: string;
  } = {
    fabricId,
    poolId,
    clusterId
  };
  if (clusterName) {
    payload.clusterName = clusterName;
  }
  return await makePostCall(`${getFabricEndPoint(executionUrl, fabricId)}/sessions`, payload);
}

export const fetchJobRunList = xhrWrapper(async function fetchJobRunList(
  executionUrl: string,
  fabricId: string,
  payload: unknown
) {
  return makePostCall(getJobRunListUrl(executionUrl, fabricId), payload) as XHRResponseType<
    {
      status: string;
      prophecyJobId: string;
      prophecyJobName: string;
      startDate: number;
      endDate: number;
      databricksRunId?: string;
      databricksJobId?: string;
      airflowDagName?: string;
      airflowDagRunId?: string;
    }[]
  >;
});

export async function fetchJobLogList(executionUrl: string, fabricId: string, payload: unknown) {
  return makePostCall(getJobLogListUrl(executionUrl, fabricId), payload) as XHRResponseType<
    {
      status: ExecutionStatus;
      name: string;
      stepId: string;
      url: string;
      logs: ExecutionLog[];
      id?: string;
      type?: 'pipeline';
    }[]
  >;
}

export const getUpgradeVersions = () => {
  return makeGetCall(`${hostUrl}/athena/api/v1/cluster/availableVersions`);
};

export const upgradeVersion = async function upgradeVersion(body: unknown) {
  return makePostCall(`${hostUrl}/api/cluster/oneupgrade`, body);
};

export const getCreditUsage = (skipCredits?: boolean) => {
  return makeGetCall(`${hostUrl}/athena/api/v1/metering${skipCredits ? '?skipCredits=true' : ''}`);
};

export const getCreditConsumption = () => {
  return makeGetCall(`${hostUrl}/api/metering/creditsUsed`);
};

export async function requestCredits({ creditsRequested, notes }: { creditsRequested: number; notes: string }) {
  return makePostCall(`${hostUrl}/athena/api/v1/controlhub`, {
    eventType: 'CREDITS_REQUEST',
    creditsRequested,
    notes
  });
}

export async function requestSeats({ seatsRequested, notes }: { seatsRequested: number; notes: string }) {
  return makePostCall(`${hostUrl}/athena/api/v1/controlhub`, {
    eventType: 'SEATS_REQUEST',
    seatsRequested,
    notes
  });
}

export async function activateUser() {
  await makePostCall(`${hostUrl}/api/user/activate`, undefined);
  navigateAndRefresh('/');
}

export async function getAllIdpConfig() {
  return await makeGetCall(APIUrls.GET_ALL_IDP_CONFIG.url);
}

export async function getSSOConfig(identifier: string, type?: AuthTypes.azuread | AuthTypes.saml) {
  const payload: {
    identifier: string;
    type?: AuthTypes.azuread | AuthTypes.saml;
  } = { identifier };
  if (type) {
    payload.type = type;
  }
  return await makePostCall(hostUrl + '/api/sso/getSSOInfoForUser', payload);
}

export async function getAllIdpTypes() {
  return await makeGetCall(`${hostUrl}/api/idp/getAllIDPsTypes`);
}

export async function setIdpConfig(payload: unknown) {
  return await makePostCall(APIUrls.SET_IDP_CONFIG.url, payload);
}

export async function setConfigureSSO(payload: unknown) {
  return await makePostCall(hostUrl + '/api/sso/setOrgForSSO', payload);
}

export async function setSSO(payload: unknown) {
  return await makePostCall(hostUrl + '/api/sso/setSSOInfo', payload);
}

export async function getAdminAllSandboxConfiguration() {
  return await makeGetCall(`${hostUrl}/api/feature/getConfig`);
}

export async function getSandboxUsageDetails() {
  return await makeGetCall(`${hostUrl}/api/sandbox/usage`);
}

export async function getSandboxDetail() {
  return await makeGetCall(`${hostUrl}/api/sandbox`);
}

export async function setAdminSandboxConfiguration({ configType, value }: AdminConfigType) {
  return await makePostCall(`${hostUrl}/api/feature/setConfig?configType=${configType}`, value);
}

export async function getAllFeatures() {
  return await makeGetCall(`${hostUrl}/api/feature/featuremgmt`);
}

export async function getAllScheduledFeatures() {
  return await makeGetCall(`${hostUrl}/api/feature/featurescheduler`);
}

export async function patchFeatures(payload: {
  featureSchedulers: { featureID: string; action: 'enabled' | 'disabled' }[];
  scheduledTime: string; // date string in ISO format
}) {
  return makePostCall(hostUrl + '/api/feature/featurescheduler', payload);
}

export async function cancelScheduledFeatures() {
  return makeDeleteCall(hostUrl + '/api/feature/featurescheduler', {});
}

export const formatExpression = async (payload: { code: string; language: BackEndCodeLanguageType }) => {
  return (await makePostCall(hostUrl + '/api/workflow/formatCode', payload)) as { status: boolean; code: string };
};

export async function authInternalGit(url: string) {
  return makePostCall(url, {});
}

export async function getGlobalConf(url: string) {
  return makeGetCall(hostUrl + '/api' + url);
}

export const getProphecyManagedFabricData = xhrWrapper(async () => {
  return makeGetCall(hostUrl + '/api/fabric/setup/default');
});

export const clonePipeline = async (payload: {
  sourceProjectUID: string;
  sourcePipelinePath: string;
  targetProjectUID: string;
  targetPipelineName: string;
  targetPackageName: string;
  targetBranch: string;
  targetConfigPackageName?: string;
  isConfigured?: boolean;
}): XHRResponseType<string> => {
  return makePostCall(hostUrl + `/api/editor/pipeline/clone`, payload);
};

export const rebuildCode = async (payload: {
  branch: string;
  projectId: string;
  pipelineIds?: string[];
}): XHRResponseType<{}> => {
  return makePostCall(hostUrl + `/api/editor/rebuildCode`, payload);
};

export async function getPlibsVersions() {
  return makeGetCall(hostUrl + '/api/editor/plibVersions') as XHRResponseType<{
    plibMavenVersions: string[];
    plibPypiVersions: string[];
    releaseVersions: { [key: string]: string };
  }>;
}
export const rebuildJobCode = async (payload: { branch: string; projectId: string }): XHRResponseType<{}> => {
  return makePostCall(hostUrl + `/api/editor/jobs/rebuildCode`, { ...payload, jobIds: [] });
};
type DeleteGemPayload = {
  name: string;
  language: SpecLanguage | AirflowSpecLanguage;
  mode?: WorkflowModeType;
  category: string;
};
export const deleteAirflowGem = xhrWrapper((payload: DeleteGemPayload) => {
  return makePostCall(APIUrls.DELETE_AIRFLOW_GEM.url, payload);
});

export const deleteGem = xhrWrapper((payload: DeleteGemPayload) => {
  return makePostCall(APIUrls.DELETE_GEM.url, payload);
});

export const fetchFabricCatalogs = async (
  payload: unknown,
  executionServiceUrl: string
): XHRResponseType<{ version: string }> => {
  return makePostCall(`${getExecutionServiceEndPoint(executionServiceUrl)}/fabric/listCatalog`, payload);
};

export const fetchFabricCatalogSchema = async (
  payload: unknown,
  executionServiceUrl: string
): XHRResponseType<{ version: string }> => {
  return makePostCall(`${getExecutionServiceEndPoint(executionServiceUrl)}/fabric/listSchema`, payload);
};

export const fetchFabricCatalogSchemaTableData = async (
  payload: unknown,
  executionServiceUrl: string
): XHRResponseType<{ version: string }> => {
  return makePostCall(`${getExecutionServiceEndPoint(executionServiceUrl)}/fabric/getTable`, payload);
};

export const fetchFabricCatalogSchemaTable = async (
  payload: unknown,
  executionServiceUrl: string
): XHRResponseType<{ version: string }> => {
  return makePostCall(`${getExecutionServiceEndPoint(executionServiceUrl)}/fabric/listTable`, payload);
};

export async function testDatabricksConnection(url: string, token?: string, fabricId?: string) {
  let params;
  if (token) {
    params = {
      url: url,
      token: token as string
    };
  } else {
    params = {
      url: url,
      fabricId: fabricId as string
    };
  }
  return makePostCall(`${hostUrl}/api/fabric/databricks/type`, params);
}

export const testAirflowConnection = async (
  payload: unknown,
  executionServiceUrl: string
): XHRResponseType<{ version: string }> => {
  return makePostCall(`${getExecutionServiceEndPoint(executionServiceUrl)}/testConnection/airflow`, payload);
};

export const fetchAirflowEnvironments = async (
  payload: unknown,
  executionServiceUrl: string
): XHRResponseType<AirflowEnvironment[]> => {
  return makePostCall(`${getExecutionServiceEndPoint(executionServiceUrl)}/fetchEnvironments`, payload);
};

export const fetchSparkEnvironments = async (
  payload: unknown,
  executionServiceUrl: string
): XHRResponseType<SparkEnvironmentResponse> => {
  return makePostCall(`${getExecutionServiceEndPoint(executionServiceUrl)}/fetchEnvironments/spark`, payload);
};

export const fetchExternalAirflowConnections = async (
  payload: unknown,
  executionServiceUrl: string
): XHRResponseType<{ connections: AirflowConnection[] }> => {
  return makePostCall(`${getExecutionServiceEndPoint(executionServiceUrl)}/fetchConnections/airflow`, payload);
};

export const fetchProjectReleases = async (
  executionUrl: string,
  projectId: string
): XHRResponseType<ProjectRelease[]> => {
  return makeGetCall(`${getExecutionServiceEndPoint(executionUrl)}/release/project/${projectId}/listReleases`);
};

export const fetchProjectRelease = async (
  executionUrl: string,
  {
    projectId,
    releaseTag
  }: {
    projectId: string;
    releaseTag: string;
  }
): XHRResponseType<ProjectRelease> => {
  return makeGetCall(
    `${getExecutionServiceEndPoint(executionUrl)}/release/project/${projectId}/releaseDetail?releaseTag=${releaseTag}`
  );
};

export const fetchProjectDeployments = async (
  executionUrl: string,
  projectId: string,
  fabricId: string
): XHRResponseType<ProjectDeployment[]> => {
  return makeGetCall(
    `${getExecutionServiceEndPoint(executionUrl)}/release/project/${projectId}/fabric/${fabricId}/listDeployments`
  );
};

export const fetchProjectDeloymentHistory = async (
  executionUrl: string,
  projectId: string
): XHRResponseType<ProjectDeploymentHistory[]> => {
  return makeGetCall(`${getExecutionServiceEndPoint(executionUrl)}/release/project/${projectId}/listDeployments`);
};

export const fetchJobDeploymentInfo = async (
  executionUrl: string,
  projectId: string,
  releaseTag: string,
  fabricId?: string
): XHRResponseType<JobDeploymentRow[]> => {
  return makeGetCall(
    `${getExecutionServiceEndPoint(executionUrl)}/release/project/${projectId}${
      fabricId ? `/fabric/${fabricId}` : ''
    }/jobsToDeploy?releaseTag=${releaseTag}`
  );
};

export const fetchJobDeploymentId = async (
  executionUrl: string,
  payload: {
    project_id: string;
    release_tag: string;
    mode: JobDeploymentMode;
    jobs_and_fabric?: Array<{ job_id: string; fabric_id: string }>;
  }
): XHRResponseType<{ deployment_id: string }> => {
  return makePostCall(`${getExecutionServiceEndPoint(executionUrl)}/release/project/deploy`, payload);
};

export const migrateEntityFabric = async (url: string, fabricId?: string): XHRResponseType<never> => {
  const serviceUrl = getExecutionServiceEndPoint(url);
  return makeGetCall(`${serviceUrl}/fabrics${fabricId ? `/${fabricId}` : ''}/info`);
};

export async function fetchPackagesList() {
  return makeGetCall(getTranspilerEndpoint() + '/list') as XHRResponseType<ImportResponse>;
}

export async function getImportChanges(transpilerId: string) {
  const url = getTranspilerEndpoint() + '/' + transpilerId + '/getEntitiesToBeImported';
  return makeGetCall(url) as XHRResponseType<ChangeEntityImportType[]>;
}

export async function getPipelinePsets(transpilerId: string, path: string) {
  const url = `${getTranspilerEndpoint()}/${transpilerId}/dependentPsets`;
  return makePostCall(url, { path }) as XHRResponseType<{ data: string[] }>;
}

export async function getBasicFormDataChanges(transpilerId: string) {
  const url = getTranspilerEndpoint() + '/' + transpilerId + '/state';
  return makeGetCall(url) as XHRResponseType<{
    createTranspilerImportData: BasicFormType;
    entitiesData: TranspilerEntityType & { source?: string };
  }>;
}

export async function getInfoCardData(transpilerId: string, isBulk?: boolean) {
  const url = getTranspilerEndpoint() + '/' + transpilerId + '/info';
  return makeGetCall(isBulk ? url + '/v2' : url) as XHRResponseType<InfoCardType>;
}

export async function getAllTranspiledData(transpilerId: string, isBulk?: boolean) {
  // need isBulk flag here
  const url = getTranspilerEndpoint() + '/' + transpilerId + '/info/all';
  return makeGetCall(isBulk ? url : url + '/v1') as XHRResponseType<{
    entities: SourceInfoType[];
  }>;
}

export async function getTranspiledPipelinesData(transpilerId: string, isBulk?: boolean) {
  const url = getTranspilerEndpoint() + '/' + transpilerId + '/info/pipelines';
  return makeGetCall(isBulk ? url : url + '/v1') as XHRResponseType<{ pipelines: PipelineInfoType[] }>;
}

export async function getTranspiledModelsData(transpilerId: string, isBulk?: boolean) {
  const url = getTranspilerEndpoint() + '/' + transpilerId + '/info/models';
  return makeGetCall(isBulk ? url : url + '/v1') as XHRResponseType<{ models: PipelineInfoType[] }>;
}

export async function getTranspiledJobsData(transpilerId: string, isBulk?: boolean) {
  const url = getTranspilerEndpoint() + '/' + transpilerId + '/info/jobs';
  return makeGetCall(isBulk ? url : url + '/v1') as XHRResponseType<{ jobs: JobInfoType[] }>;
}

export async function getTranspiledConfigsData(transpilerId: string, isBulk?: boolean) {
  const url = getTranspilerEndpoint() + '/' + transpilerId + '/info/configuration';
  return makeGetCall(isBulk ? url : url + '/v1') as XHRResponseType<{ configurations: ConfigurationInfoType[] }>;
}

export async function getUdfsData(transpilerId: string) {
  const url = getTranspilerEndpoint() + '/' + transpilerId + '/info/udfs';
  return makeGetCall(url) as XHRResponseType<{ udfs: UDfInfoType[] }>;
}

export async function getStoredProceduresData(transpilerId: string) {
  const url = getTranspilerEndpoint() + '/' + transpilerId + '/info/storedProcedures';
  return makeGetCall(url) as XHRResponseType<{ storedProcedures: SourceInfoType[] }>;
}

export async function getTranspiledDatasetsData(transpilerId: string, isBulk?: boolean) {
  const url = getTranspilerEndpoint() + '/' + transpilerId + '/info/datasets';
  return makeGetCall(isBulk ? url : url + '/v1') as XHRResponseType<{ datasets: DatasetInfoType[] }>;
}

export async function fetchTranspilerPaths(transpilerId: string) {
  return makeGetCall(getTranspilerEndpoint() + '/' + transpilerId + '/uploadedFilePaths') as XHRResponseType<string[]>;
}

export async function fetchTranspilerComponentInfo(
  transpilerId: string,
  componentId: string,
  pipelineName: string,
  isBulk?: boolean
) {
  const url = isBulk
    ? `${getTranspilerEndpoint()}/${transpilerId}/pipeline/${pipelineName}/component/${componentId}/info/v2`
    : `${getTranspilerEndpoint()}/${transpilerId}/component/${componentId}/info`;
  return makeGetCall(url) as XHRResponseType<ComponentInfo>;
}

export async function deleteTranspilerFile(transpilerId: string, isBulk?: boolean) {
  const url = `${getTranspilerEndpoint()}/${transpilerId}/delete`;
  return makePostCall(isBulk ? url : `${url}/v1`, {}) as XHRResponseType<unknown>;
}

export async function deleteUploadedFile(transpilerId: string, filePath: string) {
  return makePostCall(`${getTranspilerEndpoint()}/${transpilerId}/file/delete`, {
    filePath
  }) as XHRResponseType<unknown>;
}

export async function moveToFolder(id: string, body: { currentFilePath: string; targetFilePath: string }) {
  return makePostCall(`${getTranspilerEndpoint()}/${id}/file/move`, body) as XHRResponseType<{
    transpilerId: string;
  }>;
}

export async function getSourceEntities(id: string, body: { filePath: string; transpilerType: string }) {
  return makePostCall(
    `${getTranspilerEndpoint()}/${id}/fetchUnderlyingEntities`,
    body
  ) as XHRResponseType<TranspilerEntityType>;
}

export async function setTranspilerBasicData(body: BasicFormType, transpilerId?: string) {
  return makePostCall(
    transpilerId
      ? `${getTranspilerEndpoint()}/${transpilerId}/updateBasicDetails`
      : `${getTranspilerEndpoint()}/create`,
    { ...body, transpilerId }
  ) as XHRResponseType<{ success: boolean; transpilerId?: string }>;
}

export async function importData(id: string, body: ChangeEntityImportType[]) {
  return makePostCall(`${getTranspilerEndpoint()}/${id}/importEntities`, body) as XHRResponseType<{
    success: boolean;
  }>;
}

export async function uploadFilesToTranspile(id: string, data: unknown, onProgress: (percentage: number) => void) {
  return uploadFiles({
    url: getTranspilerEndpoint() + '/' + id + '/file/upload',
    method: 'POST',
    body: data as XMLHttpRequestBodyInit,
    onProgress: onProgress
  }) as XHRResponseType<string[]>;
}

export async function uploadSqlFiles({
  url,
  data,
  headers,
  onProgress
}: {
  url: string;
  data: FormData;
  headers?: Headers;
  onProgress: (percentage: number) => void;
}) {
  return uploadFiles({
    url,
    method: 'POST',
    body: data as XMLHttpRequestBodyInit,
    headers,
    onProgress: onProgress
  }) as XHRResponseType<string[]>;
}

export async function generateJob(id: string, data: unknown) {
  return makePostCall(
    `${getTranspilerEndpoint()}/${id}/getReferencedEntities`,
    data
  ) as XHRResponseType<TranspilerEntityType>;
}

export async function generatePsetContent(id: string, filePath: string) {
  return makePostCall(`${getTranspilerEndpoint()}/${id}/file/content`, { filePath }) as XHRResponseType<{
    content: string;
  }>;
}

export async function getAboutInfo() {
  return makeGetCall(hostUrl + '/athena/api/v1/service/status');
}

export async function getMonitoringInfo() {
  return makeGetCall(`${hostUrl}/api/service/monitoring`);
}

export async function getFeatureConfig(configType: string) {
  return makeGetCall(`${hostUrl}/api/feature/getConfig?configType=${configType}`);
}

export async function getMonitoringAlertInfo() {
  return makeGetCall(`${hostUrl}/api/alert/status`);
}

export async function inviteUser(body: unknown) {
  return await makePostCall(`${hostUrl}/api/auth/inviteUsertoTeam`, body);
}

export async function getUnSecureGlobalConfig() {
  return makeGetCall(hostUrl + '/api/unsecuredGlobalConf');
}

export async function setProjectStatus(body: {
  uid: number;
  projectStatus: string;
  emailEnabled: boolean;
  emailParams?: object;
}) {
  return await makePostCall(hostUrl + '/api/project/setStatus', body);
}

const logout = xhrWrapper(async function logout() {
  return makePostCall(hostUrl + '/api/logout', {});
});

const logoutWithoutShareTokens = xhrWrapper(async function logoutWithoutShareTokens() {
  return makePostCallWithoutShareTokens(hostUrl + '/api/logout', {});
});

export const login = xhrWrapper(async function login(body: {
  identifier: string;
  password: string;
  loginType: string;
}) {
  try {
    const data = (await makePostCall(hostUrl + '/api/authenticate', body)) as {
      token?: string;
    };
    return { data, message: 'Login successful', success: true };
  } catch (err) {
    const error = new ProphecyError('Unable to Authenticate. Check Email/Password again');
    error.stack = undefined;
    throw error;
  }
});

export const getUserType = xhrWrapper(async function getUserType(body: { identifier: string }) {
  try {
    const data = (await makePostCall(hostUrl + '/api/getUserManagedType', body)) as {
      success: boolean;
      type: AuthTypes;
      idp: ProviderType;
    };
    return { data, message: 'User type found.', success: true };
  } catch (err) {
    const error = new ProphecyError('Unable to Authenticate. Check Email again');
    error.stack = undefined;
    throw error;
  }
});

export const signUp = xhrWrapper(async function signUp(body: {
  identifier: string;
  firstName: string;
  lastName?: string;
  companyname: string;
}) {
  return makePostCall(hostUrl + '/api/auth/createAccount', body);
});

export const checkUserBot = async function checkUserBot(body: {
  token: string;
  email: string;
}): Promise<BotCheckResponse> {
  return makePostCall(hostUrl + '/api/userbotcheck', body);
};

export const discourseSSO = async function discourseSSO(params: {
  sso: string;
  sig: string;
}): Promise<{ success: boolean; redirectUrl: string; message: string }> {
  const queryString = new URLSearchParams(params).toString();
  return makeGetCall(hostUrl + '/api/discourse/sso?' + queryString);
};

export const discourseOauth = async function discourseOauth(params: {
  client_id: string;
  redirect_uri: string;
  response_type: string;
  state: string;
}): Promise<{ success: boolean; redirectUrl: string; message: string }> {
  const queryString = new URLSearchParams(params).toString();
  return makeGetCall(hostUrl + '/api/discourse/oauth2/authorize?' + queryString);
};

export const forgotPassword = xhrWrapper(async function forgotPassword(body: { identifier: string }) {
  return makePostCall(hostUrl + '/api/auth/forgotPassword', body);
});
export const resendCode = xhrWrapper(async function resendCode(body: { identifier: string }) {
  return makePostCall(hostUrl + '/api/auth/resendOTP', body);
});
export const verifyOTP = xhrWrapper(async function verifyOTP(body: {
  identifier: string;
  otp: string;
  invitation: boolean;
}) {
  return makePostCall(hostUrl + '/api/auth/verifyOTP', body);
});
export const setNewPassword = xhrWrapper(async function setNewPassword(body: {
  identifier: string;
  password: string;
  teamId?: string;
}) {
  return makePostCall(hostUrl + '/api/auth/setNewPassword', body);
});

export async function oAuthAzureADAuthenticate(body: unknown) {
  return makePostCall(hostUrl + '/api/oAuthAzureADAuthenticate', body);
}

export async function oAuthSamlAuthenticate(body: unknown) {
  return makePostCall(hostUrl + '/api/oAuthSamlAuthenticate', body);
}

export const oAuthGithubAuthenticate = xhrWrapper(async () => {
  return makePostCall(hostUrl + '/api/oAuthGithubAuthenticate', {});
});

export const getGithubOAuthInfo = xhrWrapper(async (params: queryString.StringifiableRecord) => {
  return makeGetCall(
    queryString.stringifyUrl({ url: hostUrl + '/api/githubUserInfo', query: params }, { encode: false })
  );
});

export const githubEnterpriseGenerateToken = xhrWrapper(async (host: string) => {
  return makePostCall(hostUrl + '/api/githubEnterprisePATAction?host=' + host, {});
});

export async function oAuthGoogleAuthenticate(body: unknown) {
  return makePostCall(hostUrl + '/api/oAuthGoogleAuthenticate', body);
}

export const setNewPasswordForDBUser = xhrWrapper(async function setNewPasswordForDBUser(
  body: DatabrickUserPasswordPayload,
  headers: Headers
) {
  return makePostCall(hostUrl + '/api/external/setNewPasswordForDBUser', body, headers);
});

export async function setNewEmail(body: unknown, isResend?: boolean) {
  const uri = isResend ? '/api/auth/changeEmailResendOTP' : '/api/auth/changeEmail';
  return makePostCall(hostUrl + uri, body);
}

export async function sendVerifiedOTP(body: unknown) {
  return makePostCall(hostUrl + '/api/auth/changeEmailVerifyOTP ', body);
}

export async function generateAuthToken(body: unknown) {
  return makePostCall(hostUrl + '/api/auth/generateSupportTokenForUser', body);
}

export async function changeUserPasswordAsAdmin(body: { userId: string; password: string }) {
  return makePostCall(hostUrl + '/api/auth/setPasswordAsAdmin', body);
}

export async function authenticateUsingToken(body: unknown) {
  return makePostCall(hostUrl + '/api/auth/loginUsingToken', body);
}

export async function getActivatedUserCount() {
  return makeGetCall(hostUrl + '/api/auth/getActivatedUserCount');
}

export async function autoGenerateProjectFiles(body: {
  projectId: string;
  branch: string;
  generateProjectBuildFiles: boolean;
  generateFunctionFiles: boolean;
}) {
  return makePostCall(hostUrl + '/api/editor/generateAutogeneratedFiles', body);
}

export async function upgradePlan() {
  return makePostCall(hostUrl + '/api/metering/startBilling', {});
}

export async function shareProjectLink(body: { shareId: string; message?: string; emails: string[] }) {
  return makePostCall(hostUrl + '/api/share/project/sendMail', body);
}

export async function completeLogoutAndReloadPage(isAccountDelete = false) {
  const isSharedProjectOrReplayMode = isShareMode() || isReplayMode();
  try {
    if (!isAccountDelete) {
      if (isSharedProjectOrReplayMode) {
        await logoutWithoutShareTokens();
      } else {
        await logout();
      }
    }
    setImpersonatingFlag(false);
    navigateAndRefresh(Public_Routes.SignIn.getUrl(undefined, { redirect: '' }));
    clearStorage();
    localStorage.removeItem(SHOW_JPMC_POST_LOGIN_COPILOT_POPUP_ACKNOWLEDGED);
  } catch (error) {
    errorHandler(error);
  }
}

export async function getAppSession<T>() {
  return (await makeGetCall(hostUrl + '/api/get-session')) as T;
}

export async function getShareDetails(shareId: string) {
  return (await makeGetCall(`${hostUrl}/api/share/project/details/${shareId}`)) as ShareDetails;
}

export async function getReplayDetails(replayId: string) {
  return (await makeGetCall(`${hostUrl}/api/share/replay/details/${replayId}`)) as ReplayDetails;
}

export const acceptCookies = xhrWrapper(async function acceptCookies() {
  return makePostCall(`${hostUrl}/api/set-prophecy-cookies`, undefined);
});

export async function getUser<T>() {
  return (await makeGetCall(hostUrl + '/api/get-user')) as T;
}

export async function getUserFabricStatus(executionServiceUrl: string) {
  return (await makeGetCall(`${getExecutionServiceEndPoint(executionServiceUrl)}/user/fabric/status`)) as {
    fabric: UserFabricStatus;
  };
}

export async function getAthenaTrial<T>() {
  return (await makeGetCall(hostUrl + '/athena/api/v1/trial')) as T;
}

export async function getAthenaServiceStatus<T>() {
  return (await makeGetCall(hostUrl + '/athena/api/v1/prophecy/status')) as T;
}

export async function getKeepAliveSession<T>() {
  return (await makeRestCall(hostUrl + '/api/keep-alive')) as T;
}

export async function downloadMixpanelData() {
  return await makeGetCall(hostUrl + '/api/marketingDataDump');
}

export async function createUserToken(body: { name: string; expires_at: number }) {
  return await makePostCall(hostUrl + '/api/create-user-token', body);
}

export async function getUserTokens() {
  return await makeGetCall(hostUrl + '/api/get-user-tokens');
}

export async function deleteUserToken(body: { _id: string }) {
  return await makeDeleteCall(hostUrl + `/api/delete-user-token`, body);
}

export async function regenerateSQLEditor(body: { projectId: string; branch: string }) {
  return await makePostCall(hostUrl + '/api/editor/sqlEditor/regenerate', body);
}

export async function createSqlPipeline(body: {
  projectId: string;
  branch: string;
  pipelineName: string;
  filePath?: string;
  appName?: string;
  appFilePath?: string;
}) {
  return await makePostCall(hostUrl + '/api/editor/sqlEditor/pipeline/create', body);
}

export async function generateProjectDescription(body: {
  projectId: string;
  projectType: ProjectType;
  triggerType: CopilotTriggerType;
}): Promise<{ description: string }> {
  return await makePostCall(copilotEndpoint + '/describe/project', body);
}
export async function getCodeInlineSuggestions(body: {
  files: Record<string, string>;
  currentFilePath: string;
  cursorPosition: {
    line_number: number;
    column_number: number;
  };
  userPrompt?: string;
  projectId: string;
  providerType: string;
}) {
  return await makePostCall(copilotEndpoint + '/autocomplete/sql', body);
}

export async function notifyCopilotConfigUpdate() {
  return await makePostCall(copilotEndpoint + '/config', {});
}

export async function generateCommitMessage(body: {
  gitDiff: string;
  gitLog: CommitInfo[];
  modifiedFiles: string[];
  modifiedEntities: UncommittedEntityInfo[];
  userPrompt?: string;
}): Promise<{ messages: { commitMessage: string; confidence: number }[] }> {
  return await makePostCall(copilotEndpoint + '/git/commitMessage', body);
}
type DescriptionItem = { column: string; description: string };
type SqlTableSourceType = {
  tableName: string;
  datasourceSchema: {
    type: string;
    schema: SQLType;
    interimData?: {
      data: PrimitiveType[][];
    };
  };
  projectId: string;
  projectType: 'SQL';
  providerType: SchemaProviderType;
  projectLanguage: BackEndCodeLanguageType;
};
type SqlColumnSourceType = SqlTableSourceType & {
  columnNames: string[];
};
type SparkTableSourceType = {
  tableName: string;
  datasourceSchema: {
    schema: Schema;
    interimData?: {
      data: PrimitiveType[][];
    };
  };
  projectId: string;
  projectType: 'Spark';
  projectLanguage: BackEndCodeLanguageType;
};
type SparkColumnSourceType = SparkTableSourceType & {
  columnNames: string[];
};
export async function getColumnDescription(body: { sources: (SparkColumnSourceType | SqlColumnSourceType)[] }) {
  const { data } = await (makePostCall(copilotEndpoint + '/describe/column', body) as XHRResponseType<
    Record<string, { descriptions: DescriptionItem[] }>
  >);

  return Object.keys(data).reduce(
    (acc1, key) => {
      acc1[key] = data[key].descriptions.reduce(
        (acc, item) => {
          acc[item.column] = item;
          return acc;
        },
        {} as Record<string, DescriptionItem>
      );
      return acc1;
    },
    {} as Record<string, Record<string, DescriptionItem>>
  );
}

export async function getDataSourceDescription(body: { sources: (SparkTableSourceType | SqlTableSourceType)[] }) {
  const response = await (makePostCall(copilotEndpoint + '/describe/datasource', body) as XHRResponseType<
    Record<string, { description: string }>
  >);
  const data = response.data;
  return data;
}
export async function parseFuncCall(body: { expressions: SColumn[] }) {
  return await makePostCall(hostUrl + '/api/expressions/parseFuncCall', body);
}

export async function getBusinessRuleExpression(body: { rule: BusinessRule; language: BackEndCodeLanguageType }) {
  return await makePostCall(hostUrl + '/api/expressions/getBusinessRuleExpression', body);
}

export async function generateReleaseMessage(body: {
  releaseVersion?: string;
  projectId: string;
  branch: string;
  userPrompt?: string;
  userId: string;
  commits: BranchInfoQueryType['BranchInfo']['localCommitInfo'];
}): Promise<{ messages: { releaseMessage: string; confidence: number }[] }> {
  return await makePostCall(copilotEndpoint + '/git/releaseMessage', body);
}

export async function addGem(payload: {
  name: string;
  language: SpecLanguage;
  category: string;
  mode?: WorkflowModeType;
}) {
  return await makePostCall(hostUrl + '/api/editor/addComponent', payload);
}

export async function cloneJob(payload: {
  jobURI: string;
  targetProjectUID: string;
  targetJobName: string;
  targetBranch: string;
}): XHRResponseType<string> {
  return await makePostCall(`${hostUrl}/api/editor/job/clone`, payload);
}

export async function getServiceLogsTimeFrame() {
  const response = await makeGetCall(`${hostUrl}/api/logs/time`);

  return mockXHRResponse<{
    beginTimestamp: string;
    endTimestamp: string;
  }>(response);
}

export async function getAvailableServicesWithinTimeFrame(payload: { beginTimestamp: string; endTimestamp: string }) {
  const response = await makePostCall(`${hostUrl}/api/logs/services`, payload);

  return mockXHRResponse<{
    beginTimestamp: string;
    endTimestamp: string;
    components: {
      configuration: boolean;
      sandboxes: string[] | null;
      services: string[] | null;
    };
  }>(response);
}

export async function downloadLogs(payload: {
  downloadStartTime: string;
  downloadEndTime: string;
  services: string[];
}): Promise<Response> {
  return await authorizedFetch(`${hostUrl}/api/logs/download`, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: {
      'Content-Type': 'application/json'
    }
  });
}

export async function fetchChatHistory(payload: {
  chatId: string;
  userId: string;
  projectId: string;
  branchName: string;
}): Promise<ChatHistoryResponse> {
  return await makePostCall(`${copilotEndpoint}/kinesis/chatHistory`, payload);
}

export async function deleteChatHistory(payload: { chatId: string }): Promise<void> {
  return await makeDeleteCall(`${copilotEndpoint}/kinesis/chatHistory`, payload);
}

export async function downloadAnalysisReport({ transpilerId, fileName }: { transpilerId: string; fileName?: string }) {
  const response = await authorizedFetch(`${hostUrl}/api/transpiler/${transpilerId}/analysisReport/download`, {
    method: 'GET'
  });

  if (!response.ok) {
    throw new Error('Analysis report is unavailable.');
  }

  // Extract filename from the Content-Disposition header if available
  let originalFileName = fileName;
  const contentDisposition = response.headers.get('Content-Disposition');
  const match = contentDisposition?.match(/filename="?([^"]+)"?/);
  if (match && match[1]) {
    originalFileName = match[1];
  }
  // Fallback to provided fileName if no filename is found in the headers
  originalFileName = originalFileName || 'analysis-report.xlsx';

  const blob = new Blob([await response.blob()]);
  downloadFile(blob, originalFileName);
}

export async function fetchDatabricksFunctions(
  executionUrl: string,
  fabricId: string,
  catalog?: string,
  schema?: string
) {
  return (await makePostCall(`${getExecutionServiceEndPoint(executionUrl)}/fabric/listFunctions`, {
    fabricId,
    catalog,
    schema
  })) as {
    data: {
      functionsInfo: UCCatalogMetadata[];
    };
  };
}

export async function searchDatabricksFunctions(
  executionUrl: string,
  query: string,
  fabricId: string,
  catalog?: string
) {
  return (await makePostCall(`${getExecutionServiceEndPoint(executionUrl)}/fabric/searchFunctions`, {
    filter: query,
    catalog,
    fabricId
  })) as {
    data: {
      functionsInfo: UCCatalogMetadata[];
    };
  };
}

export async function uploadSparkFile(
  executionServiceUrl: string,
  uploadId: string,
  file: File,
  onProgress: (percentage: number, error?: boolean) => void
) {
  const url = `${getExecutionServiceEndPoint(executionServiceUrl)}/spark/upload/${uploadId}`;
  const formData = new FormData();
  formData.append('file', file);

  return uploadFiles({ url, method: 'POST', body: formData, onProgress: onProgress }) as XHRResponseType<void>;
}

export const getDatabricksOAuthEnabled = async (executionServiceUrl: string): XHRResponseType<boolean> => {
  return makeGetCall(`${getExecutionServiceEndPoint(executionServiceUrl)}/oauth/setup`);
};

export const getSnowflakeOAuthEnabled = async (executionServiceUrl: string): XHRResponseType<boolean> => {
  return makeGetCall(`${getExecutionServiceEndPoint(executionServiceUrl)}/oauth/snowflake/setup`);
};

export const getOrchestrationFabricJwtToken = async (fabricId: string) => {
  return mockXHRResponse<{
    jwt: string;
  }>(await makeGetCall(`${hostUrl}/api/orchestration/token/fabric/${fabricId}`));
};

export const getOrchestrationAdminJwtToken = async () => {
  return mockXHRResponse<{
    jwt: string;
  }>(await makeGetCall(`${hostUrl}/api/orchestration/token/admin`));
};

export const getOrchestrationPropertiesSpec = async (dataPlaneUrl: string) => {
  return (await makeGetCall(`${getOrchestationEndpoint(dataPlaneUrl)}/spec/format`)) as XHRResponseType<
    {
      kind: string;
      category: string;
      sourceType: 'read' | 'write';
      uiSpec: UISpecElement;
      defaultState: {
        properties: Record<string, unknown>;
      };
    }[]
  >;
};
export async function getOrchestrationRunsFabricToken(fabrics: number[]) {
  return mockXHRResponse<{
    jwt: string;
  }>(await makePostCall(`${hostUrl}/api/orchestration/token/fabricList`, { fabricIdList: fabrics }));
}

const makePostCallWithJwt = (url: string, body: Record<string, unknown>, jwt: string) => {
  return makePostCall(url, body, { Authorization: `Bearer ${jwt}` });
};

const makeGetCallWithJwt = (url: string, jwt: string) => {
  return makeGetCall(url, { Authorization: `Bearer ${jwt}` });
};

export async function getOrchestrationPipelineRuns({
  jwt,
  projectIds,
  fabricIds,
  minTime,
  maxTime,
  runTypes,
  dataPlaneUrl
}: {
  jwt: string;
  projectIds?: string[];
  fabricIds?: string[];
  minTime?: Date;
  maxTime?: Date;
  runTypes?: string[];
  dataPlaneUrl: string;
}) {
  const _minTime = minTime?.toISOString();
  const _maxTime = maxTime?.toISOString();

  return mockXHRResponse<OrchestrationPipelineRunResponse>(
    await makePostCallWithJwt(
      `${getOrchestationEndpoint(dataPlaneUrl)}/history/pipeline`,
      { projectIds, fabricIds, minTime: _minTime, maxTime: _maxTime, runTypes },
      jwt
    )
  );
}

export async function getOrchestrationPipelineRunLogs({
  jwt,
  dataPlaneUrl,
  runId
}: {
  jwt: string;
  dataPlaneUrl: string;
  runId: string;
}) {
  return mockXHRResponse<OrchestrationPipelineRunLogsResponse>(
    await makeGetCallWithJwt(`${getOrchestationEndpoint(dataPlaneUrl)}/history/pipeline/${runId}/logs`, jwt)
  );
}

export async function markPipelineRunHasAnalysisFalse({
  jwt,
  dataPlaneUrl,
  pipeline_name,
  project_id
}: {
  jwt: string;
  dataPlaneUrl: string;
  pipeline_name: string;
  project_id: string;
}) {
  return makePostCallWithJwt(
    `${getOrchestationEndpoint(dataPlaneUrl)}/history/pipeline/logs/delete`,
    { pipeline_name, project_id },
    jwt
  );
}

export async function getOrchestrationScheduledPipelines({
  jwt,
  fabricIds,
  projectIds,
  pipelineName,
  dataPlaneUrl
}: {
  jwt: string;
  fabricIds?: string[];
  projectIds?: string[];
  pipelineName?: string;
  dataPlaneUrl: string;
}) {
  return mockXHRResponse<OrchestrationScheduledPipelineResponse>(
    await makePostCallWithJwt(
      `${getOrchestationEndpoint(dataPlaneUrl)}/history/deployed_pipelines`,
      { fabricIds, pipelineName, projectIds },
      jwt
    )
  );
}

export async function getOrchestrationDeployedProjects({
  jwt,
  fabricIds,
  projectIds,
  dataPlaneUrl
}: {
  jwt: string;
  fabricIds?: string[];
  projectIds?: string[];
  dataPlaneUrl: string;
}) {
  return mockXHRResponse<{
    projects?: {
      projectId: string;
      fabricId: string;
      releaseVersion: string;
      published: string;
      latestRun?: {
        createdAt: string;
        runStatus: string;
        errorMessage?: string;
      };
    }[];
  }>(
    await makePostCallWithJwt(
      `${getOrchestationEndpoint(dataPlaneUrl)}/history/deployed_projects`,
      { fabricIds, projectIds },
      jwt
    )
  );
}

export async function uploadToSqlFileSystem(payload: { file: File | File[] }): XHRResponseType<{ uploadID: string }> {
  const formData = new FormData();

  if (Array.isArray(payload.file)) {
    payload.file.forEach((file) => {
      formData.append('file', file);
    });
  } else {
    formData.append('file', payload.file);
  }

  return await makePostCall(`${hostUrl}/api/editor/upload`, formData);
}

export async function getAllPhysicalTablesInFabric(
  dataPlaneUrl: string,
  jwt: string,
  fabricId: string,
  connectionName?: string
) {
  return await mockXHRResponse<{
    connections: {
      connectionName: string;
      tables: string[];
    }[];
  }>(
    await makeGetCallWithJwt(
      `${getOrchestationEndpoint(dataPlaneUrl)}/kg/physicalTables/${fabricId}${connectionName ? `?connection_name=${encodeURIComponent(connectionName)}` : ''}`,
      jwt
    )
  );
}

export async function getKGCrawlStatus(dataPlaneUrl: string, jwt: string, fabricId: string, connectionName?: string) {
  return mockXHRResponse<KGCrawlStatusResponse>(
    await makeGetCallWithJwt(
      `${getOrchestationEndpoint(dataPlaneUrl)}/kg/crawler_status/${fabricId}${connectionName ? `?connectionName=${encodeURIComponent(connectionName)}` : ''}`,
      jwt
    )
  );
}

export async function cancelKGCrawl(
  dataPlaneUrl: string,
  jwt: string,
  sessionId: string,
  fabricId: string,
  connectionName?: string
) {
  return mockXHRResponse<{}>(
    makePostCallWithJwt(
      `${getOrchestationEndpoint(dataPlaneUrl)}/hook/job/cancel_crawler`,
      {
        sessionID: sessionId,
        fabricIDs: [fabricId],
        ...(connectionName ? { connectionNames: { [fabricId]: [connectionName] } } : {})
      },
      jwt
    )
  );
}

export async function triggerKGCrawl(
  dataPlaneUrl: string,
  jwt: string,
  fabricId: string,
  userId: string,
  connectionId?: string
) {
  return mockXHRResponse<{ sessionID: string }>(
    await makePostCallWithJwt(
      `${getOrchestationEndpoint(dataPlaneUrl)}/hook/job/crawler`,
      {
        fabricIDs: connectionId ? undefined : [fabricId],
        metadataUserID: userId,
        connectorIDs: connectionId ? [connectionId] : undefined,
        crawlType: 'manual'
      },
      jwt
    )
  );
}

export const getSessionsSummary = function () {
  return makeGetCall(`${hostUrl}/execution/admin/spark/connections`);
};

export const refreshSparkConnectionSummaryCache = function () {
  return makePostCall(`${hostUrl}/execution/admin/spark/connections`, {});
};

export async function buildTextFromConfigSegments(segments: string | ConfigValue[]) {
  return mockXHRResponse<{ url: string }>(
    await makePostCall(`${hostUrl}/api/editor/sql/dependencyUrl/buildFromSegments`, { segments })
  );
}

export async function buildConfigSegmentsFromText(text: string) {
  return mockXHRResponse<{ segments: string | ConfigValue[] }>(
    await makePostCall(`${hostUrl}/api/editor/sql/dependencyUrl/parseEnvVars`, { url: text })
  );
}
